/**
 * ChoreIcon primitive (Plan 02-09 / UI-SPEC §"<ChoreIcon code tintSlot size />").
 *
 * Renders one of the chore-template icons inside a tinted rounded square.
 * Default 44×44 tile size (32×32 used on phone-row variant).
 *
 * Tile background uses `color-mix(--color-m-<slot> 18%, --color-sheet)` for
 * the warm wash + `text-m-<slot>` (via inline `color`) for the foreground.
 * Inline-style (not Tailwind utility) is used here because color-mix is
 * dynamic per slot — generating every (slot × token) Tailwind utility ahead
 * of time would explode the bundle. Inline hex literals are still forbidden
 * by ESLint no-restricted-syntax; this file uses CSS variables only.
 *
 * Falls back to `Icon.Sparkle` when the chore template's `icon_key` doesn't
 * resolve — defensive default so an admin who renames an icon without
 * updating templates still sees something rendered.
 */
import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

const SLOT_FALLBACK = 'sage';

/**
 * Maps the database `chores.icon_key` value to the actual `Icon.*`
 * component. Phase 1 already shipped every chore-pictogram needed by the
 * handoff; new keys map here as templates ask for them.
 */
const ICON_BY_KEY: Record<string, IconName> = {
  vacuum: 'Vacuum',
  dishes: 'Dishes',
  bed: 'Bed',
  trash: 'Trash',
  laundry: 'Laundry',
  plant: 'Plant',
  dog: 'Dog',
  book: 'Book',
  bulb: 'Bulb',
  flame: 'Flame',
  therm: 'Therm',
  // Phase 2 chore-set extensions reuse other icons:
  sparkle: 'Sparkle',
  check: 'Check',
};

/**
 * Glyfen for en `icon_key`, uten den tonede flisen rundt. Foreslå-arkets
 * ikonrutenett (#154, steg 4c) tegner glyfen i sin egen knapp.
 */
export function choreGlyph(code: string): IconName {
  return ICON_BY_KEY[code] ?? 'Sparkle';
}

export type TintSlot = 'terracotta' | 'berry' | 'ochre' | 'sage' | 'sky' | 'plum';

interface ChoreIconProps {
  /** chores.icon_key snapshot; matched against ICON_BY_KEY → Icon.* */
  code: string;
  /** Member colour slot key — falls back to 'sage' when null. */
  tintSlot?: TintSlot | string | null;
  /** Outer tile pixel size; inner glyph renders at 50% of this. */
  size?: number;
  className?: string;
}

export function ChoreIcon({
  code,
  tintSlot = SLOT_FALLBACK,
  size = 44,
  className,
}: ChoreIconProps): ReactNode {
  const slot = tintSlot ?? SLOT_FALLBACK;
  const I = Icon[choreGlyph(code)];
  const innerSize = Math.round(size * 0.5);
  return (
    <span
      data-testid="chore-icon"
      aria-hidden
      className={[
        'inline-flex items-center justify-center rounded-md',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        background: `color-mix(in oklab, var(--color-m-${slot}) 18%, var(--color-sheet))`,
        color: `var(--color-m-${slot})`,
      }}
    >
      <I size={innerSize} />
    </span>
  );
}
